import { useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/components/shared/pagination";
import { Building2, Loader2, User, Zap } from "lucide-react";
import { IndividualPaymentDue, PaymentDuesFilters, PaymentScope, SubsPaymentDue } from "../payment-dues.entity";
import type { PayerProfile } from "@/services/stripe-payer-profile.service";
import { autopayStateKey, type RecipientAutopayState } from "@/services/vendor-autopay.service";
import AutopayStatusBadge from "./AutopayStatusBadge";
import PaymentDueActions from "./PaymentDueActions";
import PaymentHistoryModal from "./PaymentHistoryModal";
import PaymentDueSheet from "./PaymentDueSheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type RecipientType = "individual" | "subs";

interface UnifiedPaymentDuesTableProps {
  individuals: IndividualPaymentDue[];
  subs: SubsPaymentDue[];
  loading?: boolean;
  onPayViaStripe: (due: IndividualPaymentDue | SubsPaymentDue, type: RecipientType, scope: PaymentScope) => void;
  onMarkAsPaid: (due: IndividualPaymentDue | SubsPaymentDue, type: RecipientType, scope: PaymentScope) => void;
  onDownloadReport: (due: IndividualPaymentDue | SubsPaymentDue, type: RecipientType) => void;
  onUpdatePaymentTerms: (
    due: IndividualPaymentDue | SubsPaymentDue,
    type: RecipientType,
    paymentTermsDays: number
  ) => Promise<void> | void;
  markingKey: string | null;
  downloadingKey: string | null;
  stripePayingKey: string | null;
  paymentTermsSavingKey: string | null;
  activeFilters: PaymentDuesFilters;
  autopayStates: Map<string, RecipientAutopayState>;
  autopayLoading: boolean;
  payerProfile: PayerProfile | null;
  onAutopayChange: (state: RecipientAutopayState) => void;
}

interface TaggedDue {
  due: IndividualPaymentDue | SubsPaymentDue;
  type: RecipientType;
}

export default function UnifiedPaymentDuesTable({
  individuals,
  subs,
  loading,
  onPayViaStripe,
  onMarkAsPaid,
  onDownloadReport,
  onUpdatePaymentTerms,
  markingKey,
  downloadingKey,
  stripePayingKey,
  paymentTermsSavingKey,
  activeFilters,
  autopayStates,
  autopayLoading,
  payerProfile,
  onAutopayChange,
}: UnifiedPaymentDuesTableProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Combined sheet state
  const [sheetState, setSheetState] = useState<{
    open: boolean;
    due: IndividualPaymentDue | SubsPaymentDue | null;
    type: RecipientType;
  }>({
    open: false,
    due: null,
    type: "individual",
  });

  // History modal state
  const [historyModal, setHistoryModal] = useState<{
    open: boolean;
    name: string;
    id: string;
    totalAmount?: number;
    remainingAmount?: number;
  }>({
    open: false,
    name: "",
    id: "",
  });

  // Mark as paid confirmation state
  const [confirmMarkPaid, setConfirmMarkPaid] = useState<{
    open: boolean;
    due: IndividualPaymentDue | SubsPaymentDue | null;
    type: RecipientType;
    scope: PaymentScope;
  }>({
    open: false,
    due: null,
    type: "individual",
    scope: "both",
  });

  const sortedIndividuals = useMemo(
    () => [...individuals].sort((a, b) => b.remaining_amount - a.remaining_amount),
    [individuals]
  );

  const sortedSubs = useMemo(
    () => [...subs].sort((a, b) => b.remaining_amount - a.remaining_amount),
    [subs]
  );

  // Combine into a single tagged list: subs first, then individuals
  const combinedData = useMemo<TaggedDue[]>(() => {
    const empTagged: TaggedDue[] = sortedIndividuals.map((due) => ({ due, type: "individual" }));
    const subTagged: TaggedDue[] = sortedSubs.map((due) => ({ due, type: "subs" }));
    return [...subTagged, ...empTagged];
  }, [sortedIndividuals, sortedSubs]);

  const totalCount = combinedData.length;

  // Reset page when data changes
  useEffect(() => {
    setPage(1);
  }, [totalCount]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil((totalCount || 1) / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalCount, pageSize, page]);

  useEffect(() => {
    if (!sheetState.open || !sheetState.due) return;

    const updatedItem = combinedData.find(
      (item) => item.type === sheetState.type && item.due.id === sheetState.due?.id
    );
    if (updatedItem && updatedItem.due !== sheetState.due) {
      setSheetState((prev) => ({ ...prev, due: updatedItem.due }));
    }
  }, [combinedData, sheetState.open, sheetState.type, sheetState.due]);

  // Paginate the combined data
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return combinedData.slice(start, start + pageSize);
  }, [combinedData, page, pageSize]);

  // Determine which section headers to show in the paginated view
  const { showIndividualHeader, showSubsHeader, individualRows, subsRows } = useMemo(() => {
    const empRows = paginatedData.filter((item) => item.type === "individual");
    const subRows = paginatedData.filter((item) => item.type === "subs");

    const pageStart = (page - 1) * pageSize;
    const pageEnd = pageStart + pageSize;
    const subCount = sortedSubs.length;

    const showSub = subRows.length > 0 || (pageStart < subCount && subCount > 0);
    const showEmp = empRows.length > 0 || (pageEnd > subCount && sortedIndividuals.length > 0);

    return {
      showIndividualHeader: showEmp,
      showSubsHeader: showSub,
      individualRows: empRows,
      subsRows: subRows,
    };
  }, [paginatedData, page, pageSize, sortedIndividuals.length, sortedSubs.length]);

  const dueKey = (type: RecipientType, id: string) => `${type}:${id}`;

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  const handleRowClick = (due: IndividualPaymentDue | SubsPaymentDue, type: RecipientType) => {
    setSheetState({ open: true, due, type });
  };

  const handleMarkAsPaidRequest = (due: IndividualPaymentDue | SubsPaymentDue, type: RecipientType, scope: PaymentScope) => {
    setConfirmMarkPaid({ open: true, due, type, scope });
  };

  const handleConfirmMarkAsPaid = () => {
    if (confirmMarkPaid.due) {
      onMarkAsPaid(confirmMarkPaid.due, confirmMarkPaid.type, confirmMarkPaid.scope);
    }
    setConfirmMarkPaid({ open: false, due: null, type: "individual", scope: "both" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading payment dues...</span>
      </div>
    );
  }

  const renderRow = (item: TaggedDue) => {
    const { due, type } = item;
    const key = dueKey(type, due.id);
    const totalAppt = due.total_appointment_count ?? due.appointment_count;
    const totalDW = due.total_daily_work_count ?? due.daily_work_count;
    const totalAmount = due.total_amount ?? due.remaining_amount;
    const remainingApptAmount = due.remaining_appointment_amount ?? 0;
    const totalApptAmount = due.total_appointment_amount ?? remainingApptAmount;
    const remainingDWAmount = due.remaining_daily_work_amount ?? 0;
    const totalDWAmount = due.total_daily_work_amount ?? remainingDWAmount;

    return (
      <TableRow
        key={key}
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => handleRowClick(due, type)}
      >
        <TableCell className="font-medium">
          <span className="flex items-center gap-1.5">
            {due.name}
            {due.ready_for_payouts && <Zap role="img" aria-label="Ready for payouts" className="h-3.5 w-3.5 text-yellow-500" />}
          </span>
        </TableCell>
        <TableCell>
          <div>
            <span className="text-sm">{due.appointment_count} of {totalAppt} unpaid</span>
            <div className="text-xs text-muted-foreground mt-0.5">
              {formatCurrency(remainingApptAmount)} of {formatCurrency(totalApptAmount)} unpaid
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div>
            <span className="text-sm">{due.daily_work_count} of {totalDW} unpaid</span>
            <div className="text-xs text-muted-foreground mt-0.5">
              {formatCurrency(remainingDWAmount)} of {formatCurrency(totalDWAmount)} unpaid
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div>
            <span className="text-sm" data-testid={`payment-terms-${type}-${due.id}`}>
              Net {due.payment_terms_days}
            </span>
            <AutopayStatusBadge
              state={autopayStates.get(autopayStateKey(type, due.id)) ?? null}
              recipientType={type}
              recipientId={due.id}
            />
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div>
            <span className="font-semibold text-destructive">
              {formatCurrency(due.remaining_amount)}
            </span>
            <div className="text-xs text-muted-foreground mt-0.5">
              of {formatCurrency(totalAmount)} unpaid
            </div>
          </div>
        </TableCell>
        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
          <PaymentDueActions
            onPayViaStripe={(scope) => onPayViaStripe(due, type, scope)}
            onMarkAsPaid={(scope) => handleMarkAsPaidRequest(due, type, scope)}
            onDownloadReport={() => onDownloadReport(due, type)}
            hasBothTypes={due.appointment_count > 0 && due.daily_work_count > 0}
            onViewDetails={() => setSheetState({ open: true, due, type })}
            onViewPaymentHistory={() =>
              setHistoryModal({
                open: true,
                name: due.name,
                id: due.id,
                totalAmount: due.total_amount ?? due.remaining_amount,
                remainingAmount: due.remaining_amount,
              })
            }
            markingPaid={markingKey === key}
            downloading={downloadingKey === key}
            payingViaStripe={stripePayingKey === key}
            stripeEligible={Boolean(due.ready_for_payouts)}
            stripeUnavailableReason={
              due.ready_for_payouts
                ? null
                : `This ${type === "individual" ? "individual" : "subcontractor"} is not ready for payouts yet.`
            }
          />
        </TableCell>
      </TableRow>
    );
  };

  const renderEmptyRow = (message: string) => (
    <TableRow>
      <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
        {message}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Appointments</TableHead>
            <TableHead>Daily Work</TableHead>
            <TableHead>Terms</TableHead>
            <TableHead className="text-right">Amount Due</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Companies (Subcontractors) Section */}
          {showSubsHeader && (
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableCell colSpan={6} className="font-semibold text-sm py-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Companies (Subcontractors)
                </div>
              </TableCell>
            </TableRow>
          )}
          {showSubsHeader && subsRows.length === 0 && sortedSubs.length === 0 && (
            renderEmptyRow("No subcontractor payment dues found")
          )}
          {subsRows.map(renderRow)}

          {/* Individuals (Individuals) Section */}
          {showIndividualHeader && (
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableCell colSpan={6} className="font-semibold text-sm py-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Individuals (Individuals)
                </div>
              </TableCell>
            </TableRow>
          )}
          {showIndividualHeader && individualRows.length === 0 && sortedIndividuals.length === 0 && (
            renderEmptyRow("No individual payment dues found")
          )}
          {individualRows.map(renderRow)}

          {/* If absolutely no data */}
          {totalCount === 0 && !showIndividualHeader && !showSubsHeader && (
            renderEmptyRow("No payment dues found")
          )}
        </TableBody>
      </Table>

      {totalCount > 0 && (
        <div className="mt-4">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalCount}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        </div>
      )}

      {/* Combined Detail + Items Sheet */}
      <PaymentDueSheet
        isOpen={sheetState.open}
        onClose={() => setSheetState((prev) => ({ ...prev, open: false }))}
        due={sheetState.due}
        recipientType={sheetState.type}
        activeFilters={activeFilters}
        onUpdatePaymentTerms={onUpdatePaymentTerms}
        paymentTermsSavingKey={paymentTermsSavingKey}
        autopayStates={autopayStates}
        autopayLoading={autopayLoading}
        payerProfile={payerProfile}
        onAutopayChange={onAutopayChange}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal((prev) => ({ ...prev, open: false }))}
        recipientName={historyModal.name}
        recipientId={historyModal.id}
        totalAmount={historyModal.totalAmount}
        remainingAmount={historyModal.remainingAmount}
      />

      {/* Mark as Paid Confirmation Dialog */}
      <AlertDialog
        open={confirmMarkPaid.open}
        onOpenChange={(open) => {
          if (!open) setConfirmMarkPaid({ open: false, due: null, type: "individual", scope: "both" });
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Mark as Paid</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark{" "}
              {confirmMarkPaid.scope === "appointments"
                ? "turn"
                : confirmMarkPaid.scope === "daily_work"
                ? "daily work"
                : "all outstanding"}{" "}
              dues for{" "}
              <span className="font-semibold text-foreground">{confirmMarkPaid.due?.name}</span>{" "}
              as paid? This will mark{" "}
              <span className="font-semibold text-foreground">
                {formatCurrency(
                  confirmMarkPaid.scope === "appointments"
                    ? (confirmMarkPaid.due?.remaining_appointment_amount ?? 0)
                    : confirmMarkPaid.scope === "daily_work"
                    ? (confirmMarkPaid.due?.remaining_daily_work_amount ?? 0)
                    : (confirmMarkPaid.due?.remaining_amount ?? 0)
                )}
              </span>{" "}
              as paid for the selected date range. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmMarkAsPaid}>
              Mark as Paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
